import { Clock, XClose } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import type { ExpedientDayForm } from "@/features/vehicle/types";
import {
	DialogUntitledUI,
	Modal,
	ModalOverlay,
} from "@/shared/components/modals";
import { EXPEDIENT_WEEK_DAYS } from "../constants";
import { ExpedientTimeSelect } from "./expedient-time-select";

type ExpedientModalProps = {
	isOpen: boolean;
	isSaving?: boolean;
	replicateToAll: boolean;
	days: ExpedientDayForm[];
	errorsByDay: Partial<Record<number, string>>;
	onClose: () => void;
	onSubmit: () => void;
	onToggleDay: (day: number, isActive: boolean) => void;
	onChangeTime: (
		day: number,
		field: "startTime" | "endTime",
		value: string
	) => void;
	onChangeReplicateToAll: (value: boolean) => void;
};

function getFormDay(days: ExpedientDayForm[], day: number): ExpedientDayForm {
	const foundDay = days.find((item) => item.day === day);
	if (foundDay) {
		return foundDay;
	}
	return {
		day,
		startTime: "",
		endTime: "",
		isActive: false,
	};
}

export const ExpedientModal = ({
	isOpen,
	isSaving = false,
	replicateToAll,
	days,
	errorsByDay,
	onClose,
	onSubmit,
	onToggleDay,
	onChangeTime,
	onChangeReplicateToAll,
}: ExpedientModalProps) => (
	<ModalOverlay
		isOpen={isOpen}
		onOpenChange={(open) => {
			if (!open) {
				onClose();
			}
		}}
	>
		<Modal>
			<DialogUntitledUI>
				<div className="w-full max-w-[700px] overflow-hidden rounded-3xl bg-bg-primary shadow-xl">
					<div className="flex items-start justify-between border-secondary_alt border-b px-8 py-6">
						<div className="flex items-center gap-4">
							<div className="rounded-xl border border-secondary_alt bg-bg-primary p-3 shadow-xs">
								<Clock className="size-6 text-fg-tertiary" />
							</div>
							<div>
								<h3 className="font-semibold text-[16px] text-primary">
									Expediente
								</h3>
								<p className="text-[14px] text-md text-tertiary">
									Atualize horários de expediente do veículo.
								</p>
							</div>
						</div>
						<button
							aria-label="Fechar modal de expediente"
							className="cursor-pointer text-fg-quaternary hover:text-fg-tertiary"
							onClick={onClose}
							type="button"
						>
							<XClose className="size-6" />
						</button>
					</div>

					<div className="max-h-[60vh] overflow-y-auto px-8 py-6">
						<div className="flex flex-col gap-6">
							{EXPEDIENT_WEEK_DAYS.map((weekDay) => {
								const formDay = getFormDay(days, weekDay.day);
								return (
									<div
										className="flex flex-col gap-2 sm:flex-row sm:items-center"
										key={weekDay.day}
									>
										<div className="w-full sm:w-64">
											<Checkbox
												isSelected={formDay.isActive}
												label={weekDay.label}
												onChange={(isSelected) =>
													onToggleDay(weekDay.day, isSelected)
												}
												size="md"
											/>
										</div>
										<div className="flex flex-1 flex-col items-center gap-2">
											{errorsByDay[weekDay.day] ? (
												<p className="text-error-primary text-sm">
													{errorsByDay[weekDay.day]}
												</p>
											) : null}

											<div className="flex w-full gap-2">
												<div className="flex w-full flex-col gap-1">
													<ExpedientTimeSelect
														disabled={!formDay.isActive}
														label="Início"
														onChange={(selectedTime) =>
															onChangeTime(
																weekDay.day,
																"startTime",
																selectedTime
															)
														}
														value={formDay.startTime}
													/>
												</div>

												<span className="pb-3 text-fg-quaternary">-</span>

												<div className="flex w-full flex-col gap-1">
													<ExpedientTimeSelect
														disabled={!formDay.isActive}
														label="Fim"
														onChange={(selectedTime) =>
															onChangeTime(weekDay.day, "endTime", selectedTime)
														}
														value={formDay.endTime}
													/>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					<div className="flex flex-col gap-4 border-secondary_alt border-t px-8 py-6 sm:flex-row sm:items-center sm:justify-between">
						<Checkbox
							isSelected={replicateToAll}
							label="Aplicar a todos os veículos."
							onChange={onChangeReplicateToAll}
							size="md"
						/>
						<div className="flex items-center gap-3">
							<Button color="secondary" isDisabled={isSaving} onClick={onClose}>
								Cancelar
							</Button>
							<Button
								isDisabled={isSaving}
								isLoading={isSaving}
								onClick={onSubmit}
							>
								Atualizar
							</Button>
						</div>
					</div>
				</div>
			</DialogUntitledUI>
		</Modal>
	</ModalOverlay>
);
